import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { UploadSimple, SpinnerGap } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { userAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export const CsfQuickUploadButton = ({ onSuccess, testId = 'csf-quick-upload-btn', autoOpen = false }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (!autoOpen) return;
    inputRef.current?.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Browsers may block this without a user click; the visible button remains as fallback.
    try { inputRef.current?.click(); } catch (_) { /* ignore */ }
  }, [autoOpen]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('La constancia debe ser un PDF descargado del portal del SAT.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('El archivo no debe superar 5MB.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await userAPI.uploadFiscalConstancy(formData);
      const v = res.data?.verification;
      if (v?.verified) {
        toast.success('Constancia validada. Ya puedes ver cotizaciones.');
        if (refreshUser) await refreshUser();
        if (onSuccess) onSuccess();
      } else {
        toast.error((v?.errors || []).join(' ') || 'Tu constancia no pasó la validación.', { duration: 8000 });
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (d?.errors?.join(' ') || d?.message || 'Error al subir la constancia.');
      toast.error(msg, { duration: 8000 });
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept="application/pdf" className="hidden" onChange={handleFile} data-testid={`${testId}-input`} />
      <Button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-10 px-5"
        data-testid={testId}
      >
        {uploading
          ? <SpinnerGap className="w-4 h-4 mr-2 animate-spin" />
          : <UploadSimple className="w-4 h-4 mr-2" weight="bold" />}
        {uploading ? 'Validando…' : 'Subir nueva constancia'}
      </Button>
    </>
  );
};
